import React, { useState, useRef } from 'react';
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  TextField,
  MenuProps,
  FormHelperText,
} from '@mui/material';

interface DropdownProps {
  options: { id: string; label: string }[];
  selectedValue: string;
  handleChange: (event: SelectChangeEvent<string>) => void;
  placeholder?: string;
  width?: string | number;
  height?: string | number;
  label?: string;
  searchable?: boolean;
  error?: boolean;
  helperText?: string | null;
  displayEmpty?: boolean;
  name?: string | null;
  disabled?: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({
  options,
  selectedValue,
  handleChange,
  placeholder, 
  label,
  width,
  height = 48,
  searchable = false,
  error = false,
  helperText = '',
  displayEmpty = true,
  disabled = false,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  const handleSearchClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  const filteredOptions = options?.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const menuProps: Partial<MenuProps> = {
    PaperProps: {
      style: {
        maxHeight: 300,
        width: width || '100%',
      },
    },
  };

  const hasValue = selectedValue !== undefined && selectedValue !== null && selectedValue !== '';

  return (
    <FormControl
      variant="outlined"
      style={{ width: width || '100%' }}
      error={error}
      disabled={disabled}
    >
      {label && hasValue && <InputLabel>{label}</InputLabel>}

      <Select
        value={selectedValue || ''}
        onChange={handleChange}
        displayEmpty={displayEmpty}
        MenuProps={menuProps}
        label={hasValue ? label : undefined}
        renderValue={(selected) => {
          if (!selected) {
            return placeholder ? (
              <span style={{ color: 'rgba(0,0,0,0.6)' }}>
                {placeholder}
              </span>
            ) : null; 
          }
          const selectedOption = options.find((opt) => opt.id === selected);
          return selectedOption?.label || '';
        }}
        sx={{
          height: height,
          display: 'flex',
          alignItems: 'center',

          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            padding: '0 14px',
          },
        }}
      >
        {placeholder && (
          <MenuItem value="">
            <em>{placeholder}</em>
          </MenuItem>
        )}

        {searchable && (
          <MenuItem disableRipple onClick={handleSearchClick}>
            <TextField
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearchChange}
              fullWidth
              onKeyDown={handleKeyDown}
              onClick={handleSearchClick}
              size="small"
              autoFocus
            />
          </MenuItem>
        )}

        {filteredOptions.length > 0 ? (
          filteredOptions.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.label}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>No options</MenuItem>
        )}
      </Select>

      {error && helperText && (
        <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

export default Dropdown;